import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { isCustomer } from '../../api';
import './legal.css';

const STORAGE_KEY = 'c2n_age_confirmed';
// Partners/admins work in their own portals; the legal pages must stay readable before confirming.
const EXEMPT = /^\/(partner-account|admin|pos|terms|privacy)(\/|$)/;

const confirmedOnThisDevice = () => {
  try {
    return !!localStorage.getItem(STORAGE_KEY);
  } catch {
    return false;
  }
};

// Age gate before any alcohol-related content (requirements 3.1 and 13). Signed-in customers
// already confirmed their age when creating their account. The answer is remembered per device.
const AgeGate: React.FC = () => {
  const { pathname } = useLocation();
  const [confirmed, setConfirmed] = useState(() => confirmedOnThisDevice() || isCustomer());
  const [refused, setRefused] = useState(false);
  const open = !confirmed && !EXEMPT.test(pathname);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  const confirm = () => {
    try {
      localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    } catch {
      /* private mode: ask again next visit */
    }
    setConfirmed(true);
  };

  return (
    <div className="age-gate" role="dialog" aria-modal="true" aria-labelledby="age-gate-title">
      <div className="age-gate-card">
        <img src="/logo2.png" alt="Cheers2Night" className="age-gate-logo" />
        {refused ? (
          <>
            <h2 id="age-gate-title">Sorry, you can't enter yet</h2>
            <p>Cheers2Night features bars, clubs, liquor offers and pool parties, so it's only for people of legal drinking age in their state.</p>
            <button type="button" className="btn btn-dark" onClick={() => setRefused(false)}>Go back</button>
          </>
        ) : (
          <>
            <h2 id="age-gate-title">Are you of legal drinking age?</h2>
            <p>
              This site shows nightlife venues, liquor offers and pool parties. You must be of legal drinking age in the state
              you're in (between 18 and 25, depending on the state and the drink) to continue. Venues check ID at entry.
            </p>
            <div className="age-gate-actions">
              <button type="button" className="btn btn-gold" onClick={confirm}>Yes, I'm of legal drinking age</button>
              <button type="button" className="btn btn-dark" onClick={() => setRefused(true)}>No</button>
            </div>
            <p className="age-gate-fine">
              By entering you agree to our <Link to="/terms">Terms of Use</Link> and <Link to="/privacy">Privacy Policy</Link>.
              Cheers2Night does not sell or deliver alcohol. Please drink responsibly.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default AgeGate;
